"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { CheckCircle } from "lucide-react";
import {
  RIVE_ANIMATIONS,
  MOBILE_ANIMATION_CONFIG,
  DEFAULT_ANIMATION_CONFIG,
  ANIMATION_DURATIONS,
} from "../../configs/animations";
import { useMobileDetection } from "../../hooks/use-mobile-detection";

export function LoginSuccessAnimation({
  onAnimationComplete,
  duration = ANIMATION_DURATIONS.LOGIN_SUCCESS,
  showFallback = true,
  userName = "Usuario",
  transitionDuration = ANIMATION_DURATIONS.TRANSITION, // Duración de la transición de salida
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const riveInstanceRef = useRef(null);
  const [showAnimation, setShowAnimation] = useState(true);
  const [isFinishing, setIsFinishing] = useState(false); // Nuevo estado para la transición
  const [animationError, setAnimationError] = useState(false);
  const [riveLoaded, setRiveLoaded] = useState(false);
  const [, setShowLoader] = useState(true);
  const [, setCanvasSize] = useState({ width: 0, height: 0 });

  // Hook para detectar dispositivo móvil
  const { isMobile } = useMobileDetection();

  // Seleccionar la animación correcta basada en el dispositivo
  const selectedAnimation = isMobile
    ? RIVE_ANIMATIONS.LOGIN_SUCCESS_SMALL
    : RIVE_ANIMATIONS.LOGIN_SUCCESS;
  const animationConfigRef = useRef(null);
  const durationRef = useRef(duration);
  durationRef.current = duration;
  const transitionDurationRef = useRef(transitionDuration);
  transitionDurationRef.current = transitionDuration;
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  onAnimationCompleteRef.current = onAnimationComplete;

  if (!animationConfigRef.current) {
    animationConfigRef.current = isMobile
      ? MOBILE_ANIMATION_CONFIG
      : DEFAULT_ANIMATION_CONFIG;
  }

  // Función para obtener el tamaño óptimo del canvas
  const getOptimalCanvasSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const devicePixelRatio = window.devicePixelRatio || 1;

    return {
      width: width * devicePixelRatio,
      height: height * devicePixelRatio,
      displayWidth: width,
      displayHeight: height,
      pixelRatio: devicePixelRatio,
    };
  }, []);

  // Función para redimensionar el canvas
  const resizeCanvas = useCallback(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const { width, height, displayWidth, displayHeight, pixelRatio } =
      getOptimalCanvasSize();

    // Establecer el tamaño real del canvas (con pixel ratio)
    canvas.width = width;
    canvas.height = height;

    // Establecer el tamaño de visualización
    canvas.style.width = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;

    // Escalar el contexto para manejar la densidad de píxeles
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(pixelRatio, pixelRatio);
    }

    setCanvasSize({ width: displayWidth, height: displayHeight });

    // Si Rive ya está cargado, redimensionar la instancia
    if (
      riveInstanceRef.current &&
      riveInstanceRef.current.resizeDrawingSurfaceToCanvas
    ) {
      try {
        riveInstanceRef.current.resizeDrawingSurfaceToCanvas();
        /* console.log("🔄 Rive redimensionado"); */
      } catch (error) {
        console.warn("⚠️ Error redimensionando Rive:", error);
      }
    }
  }, [getOptimalCanvasSize]);

  // Función para iniciar la transición de salida
  // Lee transitionDuration/onAnimationComplete vía refs para mantenerse estable (deps []) y no
  // recrearse en cada render por cambios de esas props.
  const startFinishTransition = useCallback(() => {
    /* console.log("🎬 Iniciando transición de salida..."); */
    setIsFinishing(true);

    // Después de la transición, completar la animación
    setTimeout(() => {
      onAnimationCompleteRef.current?.();
      setShowAnimation(false);
    }, transitionDurationRef.current);
  }, []);

  // Efecto para manejar el resize de la ventana
  useEffect(() => {
    const handleResize = () => {
      resizeCanvas();
    };

    // Configurar el canvas inicial
    resizeCanvas();

    // Agregar listener de resize
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [resizeCanvas]);

  useEffect(() => {
    let mounted = true;

    const initializeRive = async () => {
      try {
        // Limpiar instancia anterior si existe
        if (riveInstanceRef.current) {
          // ❗ Rive ya existe, pero igual debemos programar el fin
          setTimeout(() => {
            if (mounted) {
              startFinishTransition();
            }
          }, durationRef.current);

          return;
        }

        if (riveInstanceRef.current) {
          /* console.log("🧹 Limpiando instancia Rive anterior..."); */
          try {
            riveInstanceRef.current.cleanup?.();
          } catch (error) {
            console.warn("⚠️ Error limpiando instancia anterior:", error);
          }
          riveInstanceRef.current = null;
        }

        // Verificar que el canvas existe
        if (!canvasRef.current) {
          console.warn("Canvas no disponible para Rive");
          setAnimationError(true);
          return;
        }

        /* console.log(
          `🎬 Inicializando Rive animation para ${
            isMobile ? "móvil" : "desktop"
          }...`,
        );
        console.log(`📁 Archivo: ${selectedAnimation}`); */

        // Asegurar que el canvas esté correctamente dimensionado
        resizeCanvas();

        // Importar Rive de forma dinámica
        const { Rive, Layout, Fit, Alignment } =
          await import("@rive-app/canvas");

        if (!mounted) return;

        // Mapear las opciones de fit
        const fitOptions = {
          cover: Fit.Cover,
          contain: Fit.Contain,
          fill: Fit.Fill,
          fitWidth: Fit.FitWidth,
          fitHeight: Fit.FitHeight,
          none: Fit.None,
          scaleDown: Fit.ScaleDown,
        };

        // Mapear las opciones de alignment
        const alignmentOptions = {
          center: Alignment.Center,
          topLeft: Alignment.TopLeft,
          topCenter: Alignment.TopCenter,
          topRight: Alignment.TopRight,
          centerLeft: Alignment.CenterLeft,
          centerRight: Alignment.CenterRight,
          bottomLeft: Alignment.BottomLeft,
          bottomCenter: Alignment.BottomCenter,
          bottomRight: Alignment.BottomRight,
        };

        // Configurar la instancia de Rive para pantalla completa con alta calidad
        const riveInstance = new Rive({
          src: selectedAnimation,
          canvas: canvasRef.current,
          autoplay: animationConfigRef.current.autoplay,
          layout: new Layout({
            fit: fitOptions[animationConfigRef.current.fit],
            alignment: alignmentOptions[animationConfigRef.current.alignment],
          }),
          onLoad: () => {
            if (!mounted) return;
            /* console.log(
              `✅ Rive animation cargada correctamente (${
                isMobile ? "móvil" : "desktop"
              })`,
            ); */
            setRiveLoaded(true);
            setAnimationError(false);

            // Redimensionar después de cargar para asegurar calidad óptima
            setShowLoader(false);

            setTimeout(() => {
              if (mounted && riveInstanceRef.current) {
                resizeCanvas();
              }
            }, 100);
          },
          onLoadError: (error) => {
            if (!mounted) return;
            console.error(
              `❌ Error cargando Rive animation (${selectedAnimation}):`,
              error,
            );
            setAnimationError(true);
            setRiveLoaded(false);
          },
          onPlay: () => {
            if (!mounted) return;
            /* console.log(
              `▶️ Rive animation iniciada (${isMobile ? "móvil" : "desktop"})`,
            ); */
          },
          onStop: () => {
            if (!mounted) return;
            /* console.log(
              `⏹️ Rive animation completada (${isMobile ? "móvil" : "desktop"})`,
            ); */
          },
        });

        riveInstanceRef.current = riveInstance;

        // Timer para iniciar la transición de salida
        setTimeout(() => {
          if (mounted) {
            startFinishTransition();
          }
        }, durationRef.current);
      } catch (error) {
        if (!mounted) return;
        console.error("❌ Error inicializando Rive:", error);
        setAnimationError(true);

        // Si hay error, usar fallback y completar más rápido
        setTimeout(
          () => {
            if (mounted) {
              startFinishTransition();
            }
          },
          Math.min(durationRef.current, 2000),
        );
      }
    };

    // Reset estados cuando cambia el dispositivo
    setAnimationError(false);

    // Inicializar con un pequeño delay para asegurar que el DOM esté listo
    setTimeout(() => {
      if (mounted) {
        initializeRive();
      }
    }, 100);

    /*     return () => {
      mounted = false;

      // Limpiar timers
      if (initTimer) clearTimeout(initTimer);
      if (animationTimer) clearTimeout(animationTimer);

      // Limpiar instancia de Rive
      if (riveInstanceRef.current) {
        try {
          riveInstanceRef.current.cleanup?.();
          riveInstanceRef.current = null;
        } catch (error) {
          console.warn("Error limpiando Rive instance:", error);
        }
      }
    }; */
    // 'duration' se lee vía durationRef (no como dependencia): el cleanup de este efecto está
    // comentado, así que re-ejecutarlo por cada cambio de duration duplicaría timers/instancias de
    // Rive. 'startFinishTransition' ahora es estable (useCallback con deps [], lee
    // transitionDuration/onAnimationComplete vía refs), así que es seguro incluirla.
  }, [selectedAnimation, startFinishTransition, resizeCanvas]);

  // Si la animación ya terminó, no mostrar nada
  if (!showAnimation) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 z-50 w-full h-full bg-linear-to-br from-purpleLight to-white overflow-hidden transition-all duration-${Math.floor(
        transitionDuration / 100,
      )} ease-out ${
        isFinishing ? "opacity-0 scale-110" : "opacity-100 scale-100"
      }`}
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#000000",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transitionDuration: `${transitionDuration}ms`,
      }}
    >
      {/* Canvas para Rive - Pantalla completa con alta calidad */}
      {!animationError && (
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 transition-all duration-${Math.floor(
            transitionDuration / 100,
          )} ease-out ${riveLoaded ? "opacity-100" : "opacity-0"} ${
            isFinishing
              ? "opacity-0 scale-105 blur-xs"
              : "opacity-100 scale-100 blur-0"
          }`}
          style={{
            width: `${isMobile ? "90%" : "100%"}`,
            height: "100%",
            display: "block",
            imageRendering: "auto", // Mejor calidad de renderizado
            position: "absolute",
            top: 0,
            left: 0,
            transitionDuration: `${transitionDuration}ms`,
            backgroundColor: "#000000",
            color: "white",
            overflow: "hidden",
          }}
        />
      )}

      {/* Fallback animation si Rive falla - Adaptado según dispositivo */}
      {animationError && showFallback && (
        <div
          className={`absolute inset-0 w-full h-full flex items-center justify-center transition-all duration-${Math.floor(
            transitionDuration / 100,
          )} ease-out ${
            isFinishing ? "opacity-0 scale-110" : "opacity-100 scale-100"
          }`}
          style={{
            transitionDuration: `${transitionDuration}ms`,
            backgroundColor: "#000000",
            color: "white",
            overflow: "hidden",
          }}
        >
          <div
            className={`relative ${
              isMobile ? "scale-50" : "scale-75 sm:scale-100 lg:scale-125"
            }`}
          >
            {/* Animación de éxito adaptada al dispositivo */}
            <div className="relative">
              <CheckCircle
                className={`${
                  isMobile
                    ? "w-24 h-24"
                    : "w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64"
                } text-green transition-all duration-${Math.floor(
                  transitionDuration / 100,
                )} ease-out ${
                  isFinishing
                    ? "animate-pulse scale-125"
                    : "animate-bounce scale-100"
                }`}
                style={{
                  transitionDuration: `${transitionDuration}ms`,
                }}
              />

              {/* Anillos de expansión responsivos */}
              <div
                className={`absolute inset-0 ${
                  isMobile
                    ? "w-24 h-24 border-2"
                    : "w-32 h-32 sm:w-48 sm:h-48 lg:w-64 lg:h-64 border-4 sm:border-8"
                } border-green rounded-full transition-all duration-${Math.floor(
                  transitionDuration / 100,
                )} ease-out ${
                  isFinishing
                    ? "animate-ping opacity-0 scale-150"
                    : "animate-ping opacity-20 scale-100"
                }`}
                style={{
                  transitionDuration: `${transitionDuration}ms`,
                }}
              ></div>
            </div>

            {/* Texto de bienvenida centrado y responsivo con transición */}
            {/* Texto de bienvenida adaptado al dispositivo */}
            <div
              className={`absolute top-full ${
                isMobile ? "mt-4" : "mt-6 sm:mt-12"
              } left-1/2 transform -translate-x-1/2 text-center transition-all duration-${Math.floor(
                transitionDuration / 100,
              )} ease-out ${
                isFinishing
                  ? "opacity-0 translate-y-8 scale-95"
                  : "opacity-100 translate-y-0 scale-100"
              }`}
              style={{
                transitionDuration: `${transitionDuration}ms`,
              }}
            >
              <h2
                className={`${
                  isMobile ? "text-xl" : "text-2xl sm:text-4xl lg:text-5xl"
                } font-bold text-purple animate-fade-in mb-2`}
              >
                ¡Bienvenido!
              </h2>
              <p
                className={`${
                  isMobile ? "text-base" : "text-lg sm:text-2xl lg:text-3xl"
                } text-tdark animate-fade-in`}
                style={{ animationDelay: "0.2s" }}
              >
                {userName}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Overlay de transición adicional para suavizar más */}
      {isFinishing && (
        <div
          className="absolute inset-0 bg-linear-to-br from-purpleLight to-white animate-fade-in"
          style={{
            animationDuration: `${transitionDuration}ms`,
            animationFillMode: "forwards",
            backgroundColor: "#000000",
            color: "white",
            overflow: "hidden",
          }}
        />
      )}

      {/* Estilos CSS personalizados */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-out {
          from {
            opacity: 1;
            transform: scale(1);
          }
          to {
            opacity: 0;
            transform: scale(1.1);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }

        .animate-fade-out {
          animation: fade-out ${transitionDuration}ms ease-out forwards;
        }

        /* Mejorar la calidad de renderizado */
        canvas {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: -moz-crisp-edges;
          image-rendering: crisp-edges;
          image-rendering: pixelated;
        }

        @media (min-width: 640px) {
          canvas {
            image-rendering: auto;
          }
        }

        /* Transiciones suaves personalizadas */
        .transition-smooth {
          transition: all ${transitionDuration}ms cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
}
